import type { SpreadDefinition } from './core';
import { createSpreadDefinition } from '../internal/builders';

export const MIND_BODY_SPIRIT_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'mind-body-spirit',
  name: 'Mind / Body / Spirit',
  slug: 'mind-body-spirit',
  category: 'special',
  description: 'Triade equilibrata per ascoltare mente, corpo e asse spirituale come tre voci distinte.',
  ritual_note: null,
  opening_invocation: null,
  closing_words: null,
  difficulty: 'beginner',
  is_lilith: false,
  tree_of_life_mapping: false,
  family: 'triad',
  intent: ['spiritual', 'healing', 'daily'],
  semantic_tags: ['self-study', 'healing'],
  layout_id: 'balanced-3',
  position_specs: [
    { role_id: 'message', name: 'Mente', description: 'Quale pensiero o cornice orienta la lettura?' },
    { role_id: 'resource', name: 'Corpo', description: 'Che cosa sta dicendo il livello incarnato?' },
    { role_id: 'truth', name: 'Spirito', description: 'Quale asse spirituale o di senso emerge?' },
  ],
  keywords: ['mente', 'corpo', 'spirito', 'equilibrio'],
  recommended_for_questions: [
    'Dove sono disallineato fra pensiero, corpo e senso?',
    'Quale asse ha bisogno di più ascolto oggi?',
  ],
  contraindications: ['Non ideale per decisioni articolate o per letture con molte parti in gioco.'],
  focus_mode_ids: [],
  ritual_intensity: 1,
});

export const GUIDA_INTERIORE_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'guida-interiore',
  name: 'Guida Interiore',
  slug: 'guida-interiore',
  category: 'special',
  description: 'Cinque carte per cogliere segnale, ostacolo, sostegno e direzione rituale.',
  ritual_note: 'Può accompagnare journaling o pratica meditativa dopo la lettura.',
  opening_invocation: null,
  closing_words: null,
  difficulty: 'beginner',
  is_lilith: false,
  tree_of_life_mapping: false,
  family: 'line',
  intent: ['spiritual', 'healing', 'clarity'],
  semantic_tags: ['ritual', 'self-study', 'healing'],
  layout_id: 'line-5',
  position_specs: [
    { role_id: 'message', description: 'Quale messaggio chiama l’attenzione?' },
    { role_id: 'obstacle', description: 'Che cosa distorce l’ascolto interiore?' },
    { role_id: 'resource', description: 'Quale sostegno è già disponibile?' },
    { role_id: 'advice', description: 'Quale pratica o postura aiuta di più?' },
    { role_id: 'integration', description: 'Che cosa conviene integrare prima di procedere?' },
  ],
  keywords: ['guida', 'ascolto', 'pratica', 'interiore'],
  recommended_for_questions: [
    'Come posso ascoltarmi con più precisione?',
    'Quale pratica sostiene meglio questa fase?',
  ],
  contraindications: ['Non ideale se vuoi solo una previsione lineare del futuro.'],
  focus_mode_ids: [],
  ritual_intensity: 2,
});

export const PERCORSO_SPIRITUALE_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'percorso-spirituale',
  name: 'Percorso spirituale / evolutivo',
  slug: 'percorso-spirituale',
  category: 'special',
  description: 'Sette carte per leggere il cammino evolutivo come processo e non come identità fissa.',
  ritual_note: 'Utile quando vuoi osservare il cammino in chiave di disciplina e maturazione.',
  opening_invocation: null,
  closing_words: null,
  difficulty: 'advanced',
  is_lilith: false,
  tree_of_life_mapping: false,
  family: 'horseshoe',
  intent: ['spiritual', 'timeline', 'open'],
  semantic_tags: ['initiatic', 'ritual', 'self-study'],
  layout_id: 'horseshoe-7',
  position_specs: [
    { role_id: 'past', description: 'Da quale memoria di cammino provieni?' },
    { role_id: 'present', description: 'Quale soglia è attiva sul cammino?' },
    { role_id: 'challenge', description: 'Quale prova chiede disciplina?' },
    { role_id: 'truth', description: 'Quale verità del cammino non può essere evitata?' },
    { role_id: 'resource', description: 'Quale supporto sostiene la continuità?' },
    { role_id: 'ritual_action', description: 'Quale pratica rende il cammino concreto?' },
    { role_id: 'outcome', description: 'Quale maturazione si prepara?' },
  ],
  keywords: ['cammino', 'evoluzione', 'disciplina', 'senso'],
  recommended_for_questions: [
    'Dove mi trovo davvero nel mio percorso?',
    'Quale passaggio evolutivo chiede responsabilità?',
  ],
  contraindications: ['Non ideale per richieste puramente quotidiane o operative.'],
  focus_mode_ids: [],
  ritual_intensity: 3,
});

export const CHIAMATA_INIZIAZIONE_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'chiamata-iniziazione',
  name: 'Chiamata / Ostacolo / Offerta / Iniziazione / Manifestazione',
  slug: 'chiamata-iniziazione',
  category: 'special',
  description: 'Cinque carte per leggere una chiamata come processo: invito, resistenza, dono, prova e incarnazione.',
  ritual_note: null,
  opening_invocation: 'Ascolto la chiamata senza gonfiarla e senza tradirla.',
  closing_words: null,
  difficulty: 'intermediate',
  is_lilith: false,
  tree_of_life_mapping: false,
  family: 'line',
  intent: ['spiritual', 'open'],
  semantic_tags: ['initiatic', 'ritual'],
  layout_id: 'line-5',
  position_specs: [
    { role_id: 'message', name: 'Chiamata', description: 'Quale invito si sta facendo sentire?' },
    { role_id: 'obstacle', name: 'Ostacolo', description: 'Che cosa resiste o distorce la chiamata?' },
    { role_id: 'resource', name: 'Offerta', description: 'Quale dono o sostegno accompagna il percorso?' },
    { role_id: 'challenge', name: 'Iniziazione', description: 'Quale prova apre davvero il passaggio?' },
    { role_id: 'outcome', name: 'Manifestazione', description: 'Come può incarnarsi la chiamata nel reale?' },
  ],
  keywords: ['chiamata', 'iniziazione', 'offerta', 'manifestazione'],
  recommended_for_questions: [
    'Che cosa mi sta chiamando davvero?',
    'Quale prova apre il passaggio che sto evitando?',
  ],
  contraindications: ['Non ideale per domande strettamente binarie o amministrative.'],
  focus_mode_ids: [],
  ritual_intensity: 3,
});

export const SPIRITUAL_SPREAD_DEFINITIONS: SpreadDefinition[] = [
  MIND_BODY_SPIRIT_SPREAD_DEFINITION,
  GUIDA_INTERIORE_SPREAD_DEFINITION,
  PERCORSO_SPIRITUALE_SPREAD_DEFINITION,
  CHIAMATA_INIZIAZIONE_SPREAD_DEFINITION,
];
